import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	companyConfigRestCrudThunks,
	companyConfigRestCrudThunksForBuyer,
} from '../../thunks/company_config_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { Content } from 'antd/lib/layout/layout';
import { Card, Checkbox, Col, Row, Spin, Table, Tooltip, message } from 'antd';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { LoadingOutlined } from '@ant-design/icons';
import DirectPermissionConfigComponent from './DirectPermissionConfigComponent';
import { PERMISSION_NAMES, getBuyerAccessDefaultConfig } from '../../utils/AuthUtils';
import posthog from 'posthog-js';
import { REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG } from '../../types/entity_add_requests';

const getFieldId = (field) => `${field.groupKey}-${field.permissionKey}-${field.roleKey}`;

const ROLE_LABLES = {
	managersAllowed: 'Manager',
	storeAssociatesAllowed: 'Store Associate',
};

const SA_DISABLED_PERMISSIONS = [PERMISSION_NAMES.CAN_SHOW_ALL_SUPPLIERS_IN_WO_CREATION];

const BuyerAdminAccessControlPage: FC<any> = ({
	currentUser,
	companySettings,
	updateCompanySettings,
	getCompanySettings,
	companySettingsFetching,
}): React.ReactElement => {
	const [currentUpdatingKey, setCurrentUpdatingKey] = useState(null);

	useEffect(() => {
		getCompanySettings(currentUser);
	}, [currentUser, getCompanySettings]);

	const permissionsChanged = useCallback(
		(key) => (e) => {
			if (currentUpdatingKey) {
				return;
			}

			const updatedSetting = {
				...companySettings,
				config: {
					...companySettings.config,
					accessPermissions: {
						...nullSafeGetOrElse('config.accessPermissions', companySettings, {}),
						[key.groupKey]: {
							...nullSafeGetOrElse(`config.accessPermissions.${key.groupKey}`, companySettings, {}),
							[key.permissionKey]: {
								...nullSafeGetOrElse(
									`config.accessPermissions.${key.groupKey}.${key.permissionKey}`,
									companySettings,
									getBuyerAccessDefaultConfig(key.permissionKey)
								),
								[key.roleKey]: e.target.checked,
							},
						},
					},
				},
			};

			setCurrentUpdatingKey(getFieldId(key));

			updateCompanySettings(updatedSetting)
				.catch(() => message.error('Unable to update Access Control!'))
				.finally(() => setCurrentUpdatingKey(null));
		},
		[companySettings, currentUpdatingKey, updateCompanySettings]
	);

	// Returns whether a role's checkbox for a permission is disabled, plus the reason to surface in a
	// tooltip. "Request New Suppliers" depends on the same role's supplier-page access and is redundant
	// once the role can add/remove suppliers outright.
	const getDisabledInfo = useCallback(
		(roleKey, permissionKey) => {
			if (permissionKey === PERMISSION_NAMES.REQUEST_NEW_SUPPLIERS) {
				const othersConfig = nullSafeGetOrElse(
					'config.accessPermissions.others',
					companySettings,
					{}
				);
				const accessPageConfig =
					othersConfig[PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE] ||
					getBuyerAccessDefaultConfig(PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE);
				const modifySuppliersConfig =
					othersConfig[PERMISSION_NAMES.MODIFY_SUPPLIERS] ||
					getBuyerAccessDefaultConfig(PERMISSION_NAMES.MODIFY_SUPPLIERS);
				if (!accessPageConfig[roleKey]) {
					return {
						disabled: true,
						reason:
							'This role needs “Access to Supplier Page” before it can request new suppliers.',
					};
				}
				if (modifySuppliersConfig[roleKey]) {
					return {
						disabled: true,
						reason:
							'This role can already add or remove suppliers, so requesting new suppliers isn’t needed.',
					};
				}
			}
			if (roleKey === 'storeAssociatesAllowed' && SA_DISABLED_PERMISSIONS.includes(permissionKey)) {
				return { disabled: true, reason: undefined };
			}
			return { disabled: false, reason: undefined };
		},
		[companySettings]
	);

	const getCheckBoxField = useCallback(
		(roleKey) => ({
			title: ROLE_LABLES[roleKey],
			dataIndex: roleKey,
			render: (cellChecked, record) => {
				const { disabled, reason } = getDisabledInfo(roleKey, record.permissionKey);
				const checkbox = (
					<Checkbox
						disabled={disabled}
						checked={cellChecked}
						onChange={permissionsChanged({ ...record, roleKey })}
					/>
				);
				return (
					<div className="flex flex-row">
						{disabled && reason ? (
							<Tooltip title={reason}>
								<span className="inline-flex">{checkbox}</span>
							</Tooltip>
						) : (
							checkbox
						)}
						{currentUpdatingKey === getFieldId({ ...record, roleKey }) && (
							<div className="ml-2">
								<Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
							</div>
						)}
					</div>
				);
			},
		}),
		[currentUpdatingKey, getDisabledInfo, permissionsChanged]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Permissions',
				dataIndex: 'label',
				width: '25%',
			},
			getCheckBoxField('storeAssociatesAllowed'),
			getCheckBoxField('managersAllowed'),
		],
		[getCheckBoxField]
	);

	const getExistingValues = useCallback(
		(groupName, permissionName) =>
			nullSafeGetOrElse(
				`config.accessPermissions.${groupName}.${permissionName}`,
				companySettings,
				getBuyerAccessDefaultConfig(permissionName)
			),
		[companySettings]
	);

	const requestToAddEnabled = !!posthog.isFeatureEnabled(REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG);

	const permissionGroupings = useMemo(
		() => [
			{
				key: 'locations',
				label: 'Locations',
				values: [
					{
						key: PERMISSION_NAMES.SHOW_LOCATION_DETAILS,
						label: 'Show Location Details',
					},
					{
						key: PERMISSION_NAMES.MODIFY_LOCATIONS,
						label: 'Modify Locations',
					},
					{
						key: PERMISSION_NAMES.MODIFY_LOCATION_TYPES,
						label: 'Modify Location Types',
					},
					{
						key: PERMISSION_NAMES.MODIFY_AREAS,
						label: 'Modify Areas',
					},
					{
						key: PERMISSION_NAMES.MODIFY_SELF_LOCATION_ACCESS,
						label: 'Modify Self Location Access',
					},
					{
						key: PERMISSION_NAMES.SHOW_INTERNAL_TECH_NOTES,
						label: 'Location Staff Notes & Attachments',
					},
					{
						key: PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS,
						label: 'Access Location Documents',
					},
					{
						key: PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
						label: 'Modify Location Documents',
					},
				],
			},
			{
				key: 'assets',
				label: 'Assets',
				values: [
					{
						key: PERMISSION_NAMES.MODIFY_ASSETS,
						label: 'Add/Modify Assets',
					},
					{
						key: PERMISSION_NAMES.DELETE_ASSETS,
						label: 'Delete Assets',
					},
					{
						key: PERMISSION_NAMES.MODIFY_ASSET_TYPES,
						label: 'Add/Modify Asset Types',
					},
					{
						key: PERMISSION_NAMES.SHOW_ASSET_PRICE,
						label: 'Show Asset Price',
					},
					{
						key: PERMISSION_NAMES.SHOW_ASSET_INTERNAL_ATTACHMENTS,
						label: 'Show Asset Attachments',
					},
					{
						key: PERMISSION_NAMES.MODIFY_ASSET_INTERNAL_ATTACHMENTS,
						label: 'Upload Asset Attachments',
					},
					{
						key: PERMISSION_NAMES.CREATE_ASSET_CYCLE_EVENTS,
						label: 'Create Asset Cycle Count Events',
					},
					{
						key: PERMISSION_NAMES.EXECUTE_ASSET_CYCLE_RUNS,
						label: 'Execute Asset Cycle Count Runs',
					},
					{
						key: PERMISSION_NAMES.VIEW_REPLACEMENT_INDEX,
						label: 'View Replacement Index',
						bucketKey: 'replacementIndex',
					},
					{
						key: PERMISSION_NAMES.UPDATE_REPLACEMENT_INDEX,
						label: 'Update Replacement Index',
						bucketKey: 'replacementIndex',
					},
					{
						key: PERMISSION_NAMES.RUN_DEEP_ANALYSIS,
						label: 'Run Deep Analysis on Replacement Index',
						bucketKey: 'replacementIndex',
					},
				],
			},
			{
				key: 'siteSurveys',
				label: 'Site Surveys',
				values: [
					{
						key: PERMISSION_NAMES.MODIFY_SITE_SURVEYS,
						label: 'Add/Modify Site Surveys',
					},
					{
						key: PERMISSION_NAMES.MODIFY_SITE_SURVEY_TEMPLATES,
						label: 'Add/Modify Site Surveys Templates',
					},
					{
						key: PERMISSION_NAMES.ADD_WALKTHROUGHS,
						label: 'Add Walkthroughs',
					},
					{
						key: PERMISSION_NAMES.DELETE_WALKTHROUGHS,
						label: 'Delete Walkthroughs',
					},
				],
			},
			{
				key: 'others',
				label: 'Suppliers',
				values: [
					{
						key: PERMISSION_NAMES.CAN_ACCESS_SUPPLIER_PAGE,
						label: 'Access to Supplier Page',
					},
					...(requestToAddEnabled
						? [
								{
									key: PERMISSION_NAMES.REQUEST_NEW_SUPPLIERS,
									label: 'Request New Suppliers',
								},
						  ]
						: []),
					{
						key: PERMISSION_NAMES.MODIFY_SUPPLIERS,
						label: 'Add Or Remove Suppliers',
					},
					{
						key: PERMISSION_NAMES.EDIT_SUPPLIER_NETWORK_ASSOCIATION,
						label: 'Edit Supplier Network Associations',
					},
					{
						key: PERMISSION_NAMES.RANK_SUPPLIERS,
						label: 'Modify Supplier Rankings',
					},
					{
						key: PERMISSION_NAMES.EDIT_SUPPLIER_CONTACT,
						label: 'Edit Supplier Contact',
					},
				],
			},
			{
				key: 'workflows',
				label: 'Workflows',
				values: [
					{
						key: PERMISSION_NAMES.VIEW_TROUBLESHOOTING,
						label: 'View Troubleshooting',
					},
					{
						key: PERMISSION_NAMES.ADD_TROUBLESHSHOOTING,
						label: 'Add Troubleshooting',
					},
					{
						key: PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING,
						label: 'Modify Troubleshooting',
					},
					{
						key: PERMISSION_NAMES.DELETE_TROUBLESHSHOOTING,
						label: 'Delete Troubleshooting',
					},
					{
						key: PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS,
						label: 'Modify Troubleshooting Associations',
					},
					{
						key: PERMISSION_NAMES.VIEW_BUDGET,
						label: 'View Budget',
					},
					{
						key: PERMISSION_NAMES.ADD_BUDGET,
						label: 'Add Budget',
					},
					{
						key: PERMISSION_NAMES.MODIFY_BUDGET,
						label: 'Modify Budget',
					},
					{
						key: PERMISSION_NAMES.DELETE_BUDGET,
						label: 'Delete Budget',
					},
				],
			},
			{
				key: 'others',
				label: 'Others',
				values: [
					{
						key: PERMISSION_NAMES.ACCESS_ANALYTICS,
						label: 'Access Analytics',
					},
					{
						key: PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER,
						label: 'Can Upload Invoices For Supplier',
					},
					{
						key: PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER,
						label: 'Can Upload Quotes For Supplier',
					},
					{
						key: PERMISSION_NAMES.EDIT_WORK_ORDERS,
						label: 'Can Edit Work Orders',
					},
					{
						key: PERMISSION_NAMES.CANCEL_WORK_ORDERS,
						label: 'Can Cancel Work Orders',
					},
					{
						key: PERMISSION_NAMES.REOPEN_WORK_ORDERS,
						label: 'Can Re-open Work Orders',
					},
					{
						key: PERMISSION_NAMES.MODIFY_WO_TASKS,
						label: 'Add/Remove Work Order Tasks',
					},
					{
						key: PERMISSION_NAMES.CAN_SHOW_ALL_SUPPLIERS_IN_WO_CREATION,
						label: 'Can Show All Suppliers In WO Creation',
					},
					{
						key: PERMISSION_NAMES.MODIFY_PM,
						label: 'Modify Planned Maintenance',
					},
					{
						key: PERMISSION_NAMES.CAN_VIEW_QUOTES,
						label: 'Can View Quotes',
					},
					{
						key: PERMISSION_NAMES.CAN_VIEW_INVOICES,
						label: 'Can View Invoices',
					},
					{
						key: PERMISSION_NAMES.VIEW_LINKED_WORK_ORDERS,
						label: 'View Linked Work Orders',
					},
					{
						key: PERMISSION_NAMES.MODIFY_LINKING_WORK_ORDERS,
						label: 'Link/Unlink Work Orders',
					},
					{
						key: PERMISSION_NAMES.VIEW_WORK_ORDER_LABELS,
						label: 'View Work Order Labels',
						bucketKey: 'labels',
					},
					{
						key: PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS,
						label: 'Add/Edit Work Order Labels',
						bucketKey: 'labels',
					},
					{
						key: PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS,
						label: 'Apply Labels To Work Orders',
						bucketKey: 'labels',
					},
				],
			},
		],
		[requestToAddEnabled]
	);

	const dataSource = useMemo(
		() =>
			permissionGroupings.map((group) => ({
				...group,
				fields: group.values.map((permission) => {
					const groupKey = (permission as any).bucketKey || group.key;
					return {
						...permission,
						groupKey,
						permissionKey: permission.key,
						...getExistingValues(groupKey, permission.key),
					};
				}),
			})),
		[getExistingValues, permissionGroupings]
	);

	return (
		<Content style={{ padding: '0 0.5em' }}>
			<Row className="rowSpacing">
				{dataSource.map((group) => (
					<Col span={24} key={group.key} className="mb-2">
						<Card title={group.label} loading={companySettingsFetching}>
							<Table columns={columns} dataSource={group.fields} showHeader pagination={false} />
						</Card>
					</Col>
				))}
				<Col span={24} key={'approvals'} className="mb-2">
					<Card title="Approvals" loading={companySettingsFetching}>
						<DirectPermissionConfigComponent />
					</Card>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,

	companySettings: state.company_config.detail,
	companySettingsFetching: state.company_config.fetching,
});

const mapDispatchToProps = (dispatch) => ({
	getCompanySettings: (contact) => dispatch(companyConfigRestCrudThunks(ROLE_TYPES.BUYER, contact)),
	updateCompanySettings: (settings) =>
		dispatch(companyConfigRestCrudThunksForBuyer.update(settings)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(BuyerAdminAccessControlPage)
);
